// src/App.js - VERSION FINALE + LAZY LOADING + TRANSITIONS + NETWORK AWARE

import React, { useEffect, Suspense, lazy } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';

// ✅ PAGES CRITIQUES (eager)
import Login from './components/Login';
import Register from './components/Register';
import Navbar from './components/Navbar';
import PrivateRoute from './components/PrivateRoute';
import { SocketProvider } from './SocketContext';
import { SocialProvider } from './contexts/SocialContext';
import { NetworkProvider } from './contexts/NetworkContext';

// 🆕 [MULTI-DEVISES] Provider devise
import { CurrencyProvider } from './contexts/CurrencyContext';

// 🆕 FALLBACK + TRANSITION
import LazyFallback from './components/LazyFallback';
import PageTransition from './components/PageTransition';

// 🎨 CSS GLOBAL
import './globalTransitions.css';

// 🍪 IMPORT DU BANDEAU COOKIES
import CookieBanner from './components/CookieBanner';

// Bootstrap & autres CSS
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import './App.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider, useTheme } from './ThemeContext';
import './i18n';

// 🔥 LAZY LOADING (considérés comme des imports → doivent être en haut)
const Dashboard = lazy(() => import('./components/Dashboard'));
const Deposit = lazy(() => import('./components/Deposit'));
const Withdrawal = lazy(() => import('./components/Withdrawal'));
const Notifications = lazy(() => import('./components/Notifications'));
const Profile = lazy(() => import('./components/Profile'));
const Leaderboard = lazy(() => import('./components/Leaderboard'));
const InvestmentConfirmation = lazy(() => import('./components/InvestmentConfirmation'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const AccountSettings = lazy(() => import('./components/AccountSettings'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const GoogleAuthSuccess = lazy(() => import('./components/GoogleAuthSuccess'));
const ReferralPopup = lazy(() => import('./components/ReferralPopup'));
const InvestmentsPage = lazy(() => import('./components/InvestmentsPage'));
const GeminiChatPage = lazy(() => import('./components/GeminiChatPage'));
const InvestmentDetailPage = lazy(() => import('./components/InvestmentDetailPage'));
const IndicatorDetail = lazy(() => import('./components/IndicatorDetail'));
const WithdrawalsPage = lazy(() => import('./components/WithdrawalsPage'));
const VerifyDeposit = lazy(() => import('./components/VerifyDeposit'));
const LeaderCommissions = lazy(() => import('./components/LeaderCommissions'));
const TransactionHistory = lazy(() => import('./components/TransactionHistory'));
const ReceiptPage = lazy(() => import('./components/ReceiptPage'));
const TransferPage = lazy(() => import('./components/TransferPage'));
const OfflineBanner = lazy(() => import('./components/OfflineBanner'));
const BroadcastBanner = lazy(() => import('./components/social/BroadcastBanner'));

// 🆕 MARKETPLACE (MARCHÉ)
const MarketplacePage = lazy(() => import('./components/MarketplacePage'));

// 🆕 SYSTÈME DE PRÊT
const LoansPage = lazy(() => import('./components/Loans/LoansPage'));

// 🆕 SYSTÈME D'OBLIGATIONS (POINT 16)
const BondsPage = lazy(() => import('./components/Bonds/BondsPage'));

// 🆕 SYSTÈME DE GAINS EN ATTENTE (POINT 17) -- AJOUT N°1
const PendingGainsPage = lazy(() => import('./components/Gains/PendingGainsPage'));

// 🆕 SOCIAL
const SocialFeed = lazy(() => import('./pages/SocialFeed'));
const PostDetail = lazy(() => import('./pages/PostDetail'));
const UserPublicProfile = lazy(() => import('./pages/UserPublicProfile'));

// 🆕 ADMIN
const AdminSocial = lazy(() => import('./pages/admin/AdminSocial'));
const AdminLegalDocuments = lazy(() => import('./pages/admin/AdminLegalDocuments'));

// ============================================================
// 🛡️ PATCHES & CONFIG (après tous les imports)
// ============================================================

// 🛡️ PATCH : empêche l'erreur insertBefore en production
if (typeof Node !== 'undefined' && Node.prototype) {
  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.warn('Blocked insertBefore on mismatched parent');
      return newNode;
    }
    return originalInsertBefore.apply(this, arguments);
  };
}

// 🌐 CONFIGURATION GLOBALE D'AXIOS
axios.defaults.baseURL = (process.env.REACT_APP_API_URL || 'https://api.insulated.pro').trim();

// 🛠️ SOLUTION POUR L'ERREUR removeChild
if (process.env.NODE_ENV === 'development') {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function(child) {
    if (child && child.parentNode !== this) {
      console.warn('Prevented removeChild error');
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };
}

// ============================================================
// 🚀 COMPOSANTS
// ============================================================

const ThemedAppContent = () => {
  const { theme } = useTheme();

  return (
    <div className={`app-wrapper ${theme}-mode`}>
      <Suspense fallback={null}>
        <OfflineBanner />
      </Suspense>

      <Suspense fallback={null}>
        <BroadcastBanner />
      </Suspense>

      <Navbar />

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={theme}
      />

      <main className="main-content">
        <Suspense fallback={<LazyFallback />}>
          <PageTransition>
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/google-auth-success" element={<GoogleAuthSuccess />} />
              <Route path="/auth/google/success" element={<GoogleAuthSuccess />} />
              <Route path="/verify-deposit" element={<VerifyDeposit />} />

              <Route path="/subscribe" element={<PrivateRoute><InvestmentConfirmation /></PrivateRoute>} />

              <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
              <Route path="/deposits" element={<PrivateRoute><Deposit /></PrivateRoute>} />
              <Route path="/withdrawals" element={<PrivateRoute><Withdrawal /></PrivateRoute>} />
              <Route path="/notifications" element={<PrivateRoute><Notifications /></PrivateRoute>} />
              <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/leaderboard" element={<PrivateRoute><Leaderboard /></PrivateRoute>} />
              <Route path="/confirm-investment/:plan/:amount" element={<PrivateRoute><InvestmentConfirmation /></PrivateRoute>} />
              <Route path="/settings" element={<PrivateRoute><AccountSettings /></PrivateRoute>} />
              <Route path="/mes-investissements" element={<PrivateRoute><InvestmentsPage /></PrivateRoute>} />
              <Route path="/marketplace" element={<PrivateRoute><MarketplacePage /></PrivateRoute>} />
              <Route path="/mes-prets" element={<PrivateRoute><LoansPage /></PrivateRoute>} />
              <Route path="/obligations" element={<PrivateRoute><BondsPage /></PrivateRoute>} />

              {/* 🆕 ROUTE POUR LES GAINS EN ATTENTE (POINT 17) -- AJOUT N°2 */}
              <Route path="/gains-en-attente" element={<PrivateRoute><PendingGainsPage /></PrivateRoute>} />

              <Route path="/gemini-chat" element={<PrivateRoute><GeminiChatPage /></PrivateRoute>} />
              <Route path="/indicators/:key" element={<PrivateRoute><IndicatorDetail /></PrivateRoute>} />
              <Route path="/mes-investissements/:id" element={<PrivateRoute><InvestmentDetailPage /></PrivateRoute>} />
              <Route path="/mes-retraits" element={<PrivateRoute><WithdrawalsPage /></PrivateRoute>} />
              <Route path="/mes-commissions" element={<PrivateRoute><LeaderCommissions /></PrivateRoute>} />
              <Route path="/historique" element={<PrivateRoute><TransactionHistory /></PrivateRoute>} />
              <Route path="/transfers" element={<PrivateRoute><TransferPage /></PrivateRoute>} />
              <Route path="/receipt/:token" element={<ReceiptPage />} />

              <Route path="/communaute" element={<PrivateRoute><SocialFeed /></PrivateRoute>} />
              <Route path="/communaute/post/:id" element={<PrivateRoute><PostDetail /></PrivateRoute>} />
              <Route path="/communaute/user/:id" element={<PrivateRoute><UserPublicProfile /></PrivateRoute>} />

              <Route path="/admin/social" element={<PrivateRoute><AdminSocial /></PrivateRoute>} />
              <Route path="/admin/legal-documents" element={<PrivateRoute><AdminLegalDocuments /></PrivateRoute>} />
            </Routes>
          </PageTransition>
        </Suspense>
      </main>

      <Suspense fallback={null}>
        <ReferralPopup />
      </Suspense>

      <CookieBanner />
    </div>
  );
};

const App = () => {
  useEffect(() => {
    // SPLASH SCREEN
    const splash = document.getElementById('app-splash');
    if (splash) {
      try { sessionStorage.setItem('splash_seen', '1'); } catch (_) {}
      setTimeout(() => {
        splash.classList.add('splash-hide');
        setTimeout(() => {
          splash.remove();
          document.documentElement.style.backgroundColor = '';
        }, 700);
      }, 400);
    }

    // Intercepteur Axios
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          delete config.headers.Authorization;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // FCM
    const registerFCMToken = async () => {
      try {
        const { requestForToken, sendTokenToBackend, shouldRegisterToken } = require('./firebase/firebase-init');
        const isLoggedIn = !!localStorage.getItem('token');
        if (isLoggedIn && shouldRegisterToken()) {
          const fcmToken = await requestForToken();
          if (fcmToken) await sendTokenToBackend(fcmToken);
        }
      } catch (error) {
        console.error('❌ Erreur FCM:', error.message);
      }
    };

    registerFCMToken();

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, []);

  return (
    <Router>
      <ThemeProvider>
        <NetworkProvider>
          <SocketProvider>
            <SocialProvider>
              <CurrencyProvider>
                <ThemedAppContent />
              </CurrencyProvider>
            </SocialProvider>
          </SocketProvider>
        </NetworkProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;